import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CircleDollarSign } from "lucide-react";

import { api } from "@/api/client";
import type { CostTimeseriesResult, Pool } from "@/api/types";
import { MetricTile } from "@/components/MetricTile";
import { MiniGauge } from "@/components/MiniGauge";
import { Section } from "@/components/Section";

interface Props {
  pool: Pool;
}

const RANGES = [
  { label: "24h × 1h", buckets: 24, bucketSeconds: 3600 },
  { label: "7d × 1h", buckets: 168, bucketSeconds: 3600 },
  { label: "30d × 1d", buckets: 30, bucketSeconds: 86_400 },
];

export function PoolCost({ pool }: Props) {
  const [rangeIdx, setRangeIdx] = useState(0);
  const range = RANGES[rangeIdx];

  const ts = useQuery({
    queryKey: ["cost-ts", "pool", pool.id, range.buckets, range.bucketSeconds],
    queryFn: () =>
      api.get<CostTimeseriesResult>(
        `/cost/timeseries?pool_id=${pool.id}&buckets=${range.buckets}&bucket_seconds=${range.bucketSeconds}`,
      ),
  });

  const data = useMemo(
    () =>
      (ts.data?.points ?? []).map((p) => ({
        ts: new Date(p.ts).toLocaleString(),
        usd: p.cost_usd,
      })),
    [ts.data],
  );

  const cap =
    pool.max_replicas * pool.max_price_per_hour * (range.buckets * range.bucketSeconds) / 3600;
  const spend = ts.data?.total_usd ?? 0;

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select
          value={rangeIdx}
          onChange={(e) => setRangeIdx(Number(e.target.value))}
        >
          {RANGES.map((r, i) => (
            <option key={r.label} value={i}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="metric-grid">
        <MetricTile
          icon={<CircleDollarSign />}
          label={`Spend · ${range.label}`}
          value={`$${spend.toFixed(2)}`}
          tone={spend > 0 ? "brand" : "neutral"}
        />
        <MetricTile
          icon={<CircleDollarSign />}
          label="vs. cap"
          value={`${cap > 0 ? ((spend / cap) * 100).toFixed(1) : "0"}%`}
          unit={`of $${cap.toFixed(2)}`}
          tone={cap > 0 && spend / cap >= 0.9 ? "warn" : "ok"}
          hint={<MiniGauge value={spend} limit={cap} width={140} />}
        />
      </div>

      <Section title="Spend trend" icon={<CircleDollarSign />}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--edge)" />
            <XAxis
              dataKey="ts"
              stroke="var(--ink-muted)"
              tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }}
              minTickGap={32}
            />
            <YAxis
              stroke="var(--ink-muted)"
              tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
            />
            <Tooltip
              contentStyle={{
                background: "var(--surface)",
                border: "1px solid var(--edge-strong)",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: "var(--ink-muted)" }}
            />
            <Line
              type="monotone"
              dataKey="usd"
              name="USD"
              stroke="var(--brand)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Section>
    </>
  );
}
